import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, ChevronLeft, ChevronRight, MapPin, Ruler, Calendar, Layers, Maximize2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FILTERS } from "@/lib/content";
import { useTexts } from "@/lib/texts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.16, 1, 0.3, 1];

export const photoUrl = (p) => (p && p.startsWith("/api/") ? `${BACKEND_URL}${p}` : p);

export const Portfolio = () => {
    const texts = useTexts();
    const [projects, setProjects] = useState([]);
    const [filter, setFilter] = useState("tutti");
    const [selected, setSelected] = useState(null);
    const [photoIndex, setPhotoIndex] = useState(0);
    const [fullscreen, setFullscreen] = useState(false);

    useEffect(() => {
        axios
            .get(`${BACKEND_URL}/api/projects`)
            .then((res) => setProjects(res.data))
            .catch(() => setProjects([]));
    }, []);

    const visible = filter === "tutti" ? projects : projects.filter((p) => p.category === filter);

    const open = (p) => {
        setSelected(p);
        setPhotoIndex(0);
        setFullscreen(false);
    };

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setFullscreen(false);
            if (e.key === "ArrowRight") setPhotoIndex((i) => (i + 1) % nPhotosRef.current);
            if (e.key === "ArrowLeft") setPhotoIndex((i) => (i - 1 + nPhotosRef.current) % nPhotosRef.current);
            e.stopPropagation();
        };
        window.addEventListener("keydown", onKey, { capture: true });
        return () => window.removeEventListener("keydown", onKey, { capture: true });
    }, [fullscreen]);

    const nPhotos = selected?.photos?.length || 0;
    const nPhotosRef = useRef(nPhotos);
    nPhotosRef.current = nPhotos;
    const step = (d) => setPhotoIndex((i) => (i + d + nPhotos) % nPhotos);

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="relative bg-[#1C1917] py-20 text-stone-100 sm:py-28 lg:py-36"
        >
            <div className="grain pointer-events-none absolute inset-0" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <div className="mb-12 flex flex-wrap items-end justify-between gap-8">
                    <div>
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, ease: EASE }}
                            className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#E75C15]"
                        >
                            Realizzazioni
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                            className="mt-4 max-w-2xl font-display text-3xl font-light tracking-tight sm:text-4xl lg:text-5xl"
                        >
                            {texts.portfolio_heading}
                        </motion.h2>
                    </div>

                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtri portfolio">
                        {FILTERS.map((f) => (
                            <button
                                key={f.id}
                                data-testid={`portfolio-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`relative rounded-full border px-4 py-2 font-mono-alt text-[11px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                                    filter === f.id
                                        ? "border-[#E75C15] bg-[#E75C15] text-[#1C1917]"
                                        : "border-stone-100/20 text-stone-400 hover:border-[#E75C15]/60 hover:text-stone-100"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p, i) => (
                            <motion.article
                                layout
                                key={p.id}
                                initial={{ opacity: 0, y: 32 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.6, delay: i * 0.05, ease: EASE }}
                                data-testid={`portfolio-card-${p.id}`}
                                className="group cursor-pointer"
                                onClick={() => open(p)}
                            >
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={photoUrl(p.photos?.[0])}
                                        alt={`${p.title} · ${p.location}`}
                                        loading="lazy"
                                        decoding="async"
                                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-[#1C1917]/0 transition-colors duration-500 group-hover:bg-[#1C1917]/35" />
                                    <span className="absolute right-4 top-4 flex h-10 w-10 translate-y-2 items-center justify-center rounded-full bg-[#E75C15] text-[#1C1917] opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                                        <ArrowUpRight className="h-4 w-4" />
                                    </span>
                                    <span className="absolute left-4 top-4 border border-[#E75C15]/50 bg-[#1C1917]/70 px-3 py-1 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#E75C15] backdrop-blur-sm">
                                        {p.year}
                                    </span>
                                    {p.photos?.length > 1 && (
                                        <span className="absolute bottom-4 right-4 bg-[#1C1917]/70 px-2.5 py-1 font-mono-alt text-[10px] tracking-widest text-stone-200 backdrop-blur-sm">
                                            {p.photos.length} foto
                                        </span>
                                    )}
                                </div>
                                <div className="mt-4 flex items-start justify-between gap-4">
                                    <div>
                                        <h3 className="font-display text-xl font-medium">{p.title}</h3>
                                        <p className="mt-1 font-mono-alt text-[11px] uppercase tracking-[0.15em] text-stone-400">
                                            {p.location}{p.specs ? ` · ${p.specs}` : ""}
                                        </p>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent
                    data-testid="portfolio-lightbox"
                    className="max-h-[92vh] w-[96vw] max-w-6xl overflow-y-auto border border-[#E75C15]/30 bg-[#1C1917] p-0 text-stone-100 sm:rounded-none [&>button]:rounded-full [&>button]:bg-[#1C1917]/80 [&>button]:p-2.5 [&>button]:text-stone-100 [&>button]:opacity-100 [&>button]:transition-colors [&>button]:hover:bg-[#B8430D]"
                >
                    {selected && (
                        <>
                        <div className="grid grid-cols-1 md:grid-cols-5">
                            <div className="relative min-h-[340px] bg-black/40 md:col-span-3 md:min-h-[600px]">
                                <img
                                    src={photoUrl(selected.photos[photoIndex])}
                                    alt={`${selected.title} — foto ${photoIndex + 1}`}
                                    className="absolute inset-0 h-full w-full cursor-zoom-in object-cover"
                                    data-testid="lightbox-photo"
                                    onClick={() => setFullscreen(true)}
                                />
                                <button
                                    data-testid="lightbox-expand"
                                    onClick={() => setFullscreen(true)}
                                    className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100 transition-colors hover:bg-[#E75C15] hover:text-[#1C1917]"
                                    aria-label="Visualizza a schermo intero"
                                >
                                    <Maximize2 className="h-4 w-4" />
                                </button>
                                {nPhotos > 1 && (
                                    <>
                                        <button
                                            data-testid="lightbox-prev"
                                            onClick={() => step(-1)}
                                            className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100 transition-colors hover:bg-[#E75C15] hover:text-[#1C1917]"
                                            aria-label="Foto precedente"
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        <button
                                            data-testid="lightbox-next"
                                            onClick={() => step(1)}
                                            className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#1C1917]/80 text-stone-100 transition-colors hover:bg-[#E75C15] hover:text-[#1C1917]"
                                            aria-label="Foto successiva"
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                        <span
                                            data-testid="lightbox-counter"
                                            className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-[#1C1917]/80 px-3 py-1 font-mono-alt text-[10px] tracking-widest text-stone-200"
                                        >
                                            {photoIndex + 1} / {nPhotos}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div className="p-8 sm:p-10 md:col-span-2">
                                <DialogTitle className="font-display text-3xl font-medium">
                                    {selected.title}
                                </DialogTitle>
                                <div className="mt-6 space-y-4">
                                    <div className="flex items-center gap-3 text-sm text-stone-300">
                                        <MapPin className="h-4 w-4 text-[#E75C15]" />
                                        {selected.location}
                                    </div>
                                    <div className="flex items-center gap-3 text-sm text-stone-300">
                                        <Calendar className="h-4 w-4 text-[#E75C15]" />
                                        Consegna {selected.year}
                                    </div>
                                    {selected.specs && (
                                        <div className="flex items-center gap-3 text-sm text-stone-300">
                                            <Ruler className="h-4 w-4 text-[#E75C15]" />
                                            {selected.specs}
                                        </div>
                                    )}
                                    {selected.materials && (
                                        <div className="flex items-start gap-3 text-sm text-stone-300">
                                            <Layers className="mt-0.5 h-4 w-4 shrink-0 text-[#E75C15]" />
                                            <span>
                                                <span className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-stone-500">
                                                    Materiali
                                                </span>
                                                <br />
                                                {selected.materials}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <p data-testid="lightbox-quote" className="mt-8 border-l-2 border-[#E75C15] pl-4 font-display text-lg italic text-stone-300">
                                    “{selected.quote || "Ogni dettaglio è stato curato come se fosse casa nostra."}”
                                </p>
                            </div>
                        </div>
                        {fullscreen &&
                            createPortal(
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                    data-testid="lightbox-fullscreen"
                                    className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0C0A09]/95 p-0"
                                    style={{ pointerEvents: "auto" }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={() => setFullscreen(false)}
                                >
                                    <motion.img
                                        key={photoIndex}
                                        initial={{ opacity: 0, scale: 0.97 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.35, ease: EASE }}
                                        src={photoUrl(selected.photos[photoIndex])}
                                        alt={`${selected.title} — foto ${photoIndex + 1}`}
                                        className="h-full w-full object-contain"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <button
                                        data-testid="fullscreen-close"
                                        onClick={() => setFullscreen(false)}
                                        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-stone-100/10 text-stone-100 transition-colors hover:bg-[#E75C15] hover:text-[#1C1917]"
                                        aria-label="Chiudi schermo intero"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                    {nPhotos > 1 && (
                                        <>
                                            <button
                                                data-testid="fullscreen-prev"
                                                onClick={(e) => { e.stopPropagation(); step(-1); }}
                                                className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#1C1917]/70 text-stone-100 backdrop-blur-sm transition-colors hover:bg-[#E75C15] hover:text-[#1C1917] sm:left-5 sm:h-14 sm:w-14"
                                                aria-label="Foto precedente"
                                            >
                                                <ChevronLeft className="h-6 w-6" />
                                            </button>
                                            <button
                                                data-testid="fullscreen-next"
                                                onClick={(e) => { e.stopPropagation(); step(1); }}
                                                className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#1C1917]/70 text-stone-100 backdrop-blur-sm transition-colors hover:bg-[#E75C15] hover:text-[#1C1917] sm:right-5 sm:h-14 sm:w-14"
                                                aria-label="Foto successiva"
                                            >
                                                <ChevronRight className="h-6 w-6" />
                                            </button>
                                            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-[#1C1917]/70 px-4 py-1.5 font-mono-alt text-xs tracking-widest text-stone-200 backdrop-blur-sm">
                                                {photoIndex + 1} / {nPhotos}
                                            </span>
                                        </>
                                    )}
                                    </motion.div>,
                                    document.body
                                )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
};
